import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { HttpHelpService, UserNotificationItem } from 'src/app/Data/service/http-help.service';
import { FirebaseFcmService } from 'src/app/firebase/firebase-fcm.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { pushTagFromFcmData } from '../utils/push-notification.util';
import {
  notificationHasDirectLink,
  readNotificationUserType,
  resolveNotificationNavigationUrl,
} from '../utils/notification-url.util';

export type PushDeviceStatus = {
  device_id: string;
  device_type: string | null;
  notifications_enabled: boolean;
  has_device_token: boolean;
};

export type NotificationLogState = {
  items: UserNotificationItem[];
  total: number;
  page: number;
  loading: boolean;
  hasMore: boolean;
  error: string | null;
};

const NOTIF_PAGE_SIZE = 15;
const STATUS_TTL_MS = 60_000;

/**
 * Merchant push uses PUT /users/push-device/current/* (not /notifications/device-token).
 * Per-type user notification preferences UI is deferred until backend exposes GET/PATCH endpoint.
 */
@Injectable({ providedIn: 'root' })
export class PushDeviceService {
  readonly status$ = new BehaviorSubject<PushDeviceStatus | null>(null);
  readonly unreadCount$ = new BehaviorSubject<number>(0);
  readonly notifLog$ = new BehaviorSubject<NotificationLogState>({
    items: [],
    total: 0,
    page: 1,
    loading: false,
    hasMore: false,
    error: null,
  });

  private loading = false;
  private fcmSub: Subscription | null = null;
  private statusInFlight: Promise<PushDeviceStatus | null> | null = null;
  private lastStatusAt = 0;
  private sessionInitPromise: Promise<void> | null = null;

  constructor(
    private readonly http: HttpHelpService,
    private readonly firebaseFcm: FirebaseFcmService,
    private readonly translate: TranslateService,
    private readonly toastr: ToastrService,
    private readonly router: Router,
  ) {
    // When a foreground FCM arrives, prepend it to the log and bump the unread badge immediately
    this.fcmSub = this.firebaseFcm.foregroundMessage$.subscribe((msg) => {
      if (!this.isUserSignedIn()) {
        return;
      }
      const data = msg.rawData || {};
      const newItem: UserNotificationItem = {
        id: data.notification_id ? Number(data.notification_id) : Date.now(),
        title: msg.title,
        message: msg.body,
        url: msg.url || null,
        is_read: false,
        read_at: null,
        notification_type: data.type || data.notification_type || null,
        notification_source: (data.notification_source as UserNotificationItem['notification_source']) || null,
        task_id: data.task_id != null ? Number(data.task_id) : null,
        message_id: data.message_id != null ? Number(data.message_id) : null,
        push_tag: pushTagFromFcmData(data) || data.tag || null,
        created_at: new Date().toISOString(),
      };
      const log = this.notifLog$.value;
      this.notifLog$.next({
        ...log,
        items: [newItem, ...log.items],
        total: log.total + 1,
      });
      this.unreadCount$.next(this.unreadCount$.value + 1);
    });
  }

  get isEnabled(): boolean {
    return this.status$.value?.notifications_enabled === true;
  }

  /**
   * Single post-login bootstrap: device status, inbox, unread badge.
   * Idempotent — safe to call from login, AppComponent, and OTP flows.
   */
  initAfterLogin(options?: { syncToken?: boolean }): Promise<void> {
    if (!this.isUserSignedIn()) {
      return Promise.resolve();
    }
    if (this.sessionInitPromise) {
      return this.sessionInitPromise;
    }

    this.sessionInitPromise = (async () => {
      await this.refreshStatus({ force: true });
      if (!this.status$.value) {
        this.status$.next(this.defaultStatus());
      }
      if (options?.syncToken) {
        await this.syncTokenIfEnabled();
      }
      await Promise.all([
        this.loadNotifications(true),
        this.refreshUnreadCount(),
      ]);
    })().catch((err) => {
      console.warn('[PushDevice] initAfterLogin failed:', err);
      if (!this.status$.value) {
        this.status$.next(this.defaultStatus());
      }
    });

    return this.sessionInitPromise;
  }

  async refreshStatus(options?: { force?: boolean }): Promise<PushDeviceStatus | null> {
    if (!this.isUserSignedIn()) {
      return null;
    }

    const force = options?.force === true;
    const now = Date.now();
    const cached = this.status$.value;
    if (!force && cached && now - this.lastStatusAt < STATUS_TTL_MS) {
      return cached;
    }
    if (!force && this.statusInFlight) {
      return this.statusInFlight;
    }

    this.statusInFlight = this.fetchStatus();
    try {
      return await this.statusInFlight;
    } finally {
      this.statusInFlight = null;
    }
  }

  /** Refresh FCM token when this device is already enabled (silent, no permission prompt). */
  async syncTokenIfEnabled(): Promise<void> {
    const status = this.status$.value ?? (await this.refreshStatus());
    if (!status?.notifications_enabled) {
      return;
    }
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return;
    }
    const token = await this.firebaseFcm.obtainFcmToken(false);
    if (!token) {
      return;
    }
    try {
      await firstValueFrom(
        this.http.enableCurrentPushDevice({ device_type: 'web', device_token: token }),
      );
      await this.refreshStatus({ force: true });
    } catch (err) {
      console.warn('[PushDevice] syncTokenIfEnabled failed:', err);
    }
  }

  async enableForCurrentDevice(): Promise<boolean> {
    if (this.loading) {
      return false;
    }
    this.loading = true;
    try {
      if (typeof Notification === 'undefined') {
        this.showBrowserBlockedToast();
        return false;
      }

      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      if (permission === 'denied') {
        this.showBrowserBlockedToast();
        return false;
      }

      if (permission !== 'granted') {
        this.showBrowserBlockedToast();
        return false;
      }

      const token = await this.firebaseFcm.obtainFcmToken(true);
      if (!token) {
        this.toastr.warning(
          this.translate.instant('notifications.deviceEnableFailed'),
          this.translate.instant('notifications.deviceBellTitle'),
        );
        return false;
      }

      const res = await firstValueFrom(
        this.http.enableCurrentPushDevice({ device_type: 'web', device_token: token }),
      );
      if (res?.status === 200) {
        await this.refreshStatus({ force: true });
        this.toastr.success(
          this.translate.instant('notifications.deviceEnabledToast'),
          this.translate.instant('notifications.deviceBellTitle'),
        );
        return true;
      }

      this.toastr.warning(
        res?.messageEn || this.translate.instant('notifications.deviceEnableFailed'),
        this.translate.instant('notifications.deviceBellTitle'),
      );
      return false;
    } catch (err) {
      console.warn('[PushDevice] enable failed:', err);
      this.toastr.warning(
        this.translate.instant('notifications.deviceEnableFailed'),
        this.translate.instant('notifications.deviceBellTitle'),
      );
      return false;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Unregister push for this device on sign-out (silent — no toast).
   * Must run while the auth token is still in storage so the API call succeeds.
   */
  async disableOnSignOut(): Promise<void> {
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem('token')) {
        await firstValueFrom(this.http.disableCurrentPushDevice());
      }
    } catch (err) {
      console.warn('[PushDevice] disableOnSignOut failed:', err);
    } finally {
      this.resetLocalState();
    }
  }

  async disableForCurrentDevice(): Promise<boolean> {
    if (this.loading) {
      return false;
    }
    this.loading = true;
    try {
      const res = await firstValueFrom(this.http.disableCurrentPushDevice());
      if (res?.status === 200) {
        await this.refreshStatus({ force: true });
        this.toastr.info(
          this.translate.instant('notifications.deviceDisabledToast'),
          this.translate.instant('notifications.deviceBellTitle'),
        );
        return true;
      }
      return false;
    } catch (err) {
      console.warn('[PushDevice] disable failed:', err);
      return false;
    } finally {
      this.loading = false;
    }
  }

  async toggleForCurrentDevice(): Promise<void> {
    const status = this.status$.value ?? (await this.refreshStatus());
    if (status?.notifications_enabled) {
      await this.disableForCurrentDevice();
    } else {
      await this.enableForCurrentDevice();
    }
  }

  async refreshUnreadCount(): Promise<void> {
    try {
      const res = await firstValueFrom(this.http.getMyUnreadNotificationCount());
      this.unreadCount$.next(res?.count ?? 0);
    } catch {
      // silently ignore — unread count is non-critical
    }
  }

  async loadNotifications(reset = false): Promise<void> {
    const current = this.notifLog$.value;
    if (current.loading) return;

    const nextPage = reset ? 1 : current.page;
    this.notifLog$.next({ ...current, loading: true, error: null });

    try {
      const res = await firstValueFrom(
        this.http.getMyNotifications(nextPage, NOTIF_PAGE_SIZE),
      );

      if (!res || !Array.isArray(res.data)) {
        console.error('[NotifLog] Unexpected response shape:', res);
        this.notifLog$.next({ ...this.notifLog$.value, loading: false, error: 'unexpected_response' });
        return;
      }

      const newItems = reset ? res.data : [...current.items, ...res.data];
      this.notifLog$.next({
        items: newItems,
        total: res.total ?? 0,
        page: nextPage + 1,
        loading: false,
        hasMore: newItems.length < (res.total ?? 0),
        error: null,
      });
      this.unreadCount$.next(res.unread ?? 0);
    } catch (err: any) {
      const status = err?.status ?? err?.error?.statusCode ?? 0;
      console.error('[NotifLog] loadNotifications failed. status=' + status, err);
      this.notifLog$.next({ ...this.notifLog$.value, loading: false, error: String(status || 'network') });
    }
  }

  async markAllRead(): Promise<void> {
    try {
      await firstValueFrom(this.http.markAllNotificationsRead());
      this.unreadCount$.next(0);
      const log = this.notifLog$.value;
      this.notifLog$.next({
        ...log,
        items: log.items.map((n) => ({ ...n, is_read: true })),
      });
    } catch {
      // non-critical
    }
  }

  async markOneRead(id: number): Promise<void> {
    try {
      await firstValueFrom(this.http.markNotificationRead(id));
      this.markNotificationsReadByIds([id]);
    } catch {
      // non-critical
    }
  }

  /**
   * Mark read (if needed), navigate to the best target route, and give feedback
   * when the notification had no explicit backend link.
   */
  async openNotificationItem(item: UserNotificationItem): Promise<UserNotificationItem> {
    const hadDirectLink = notificationHasDirectLink(item);
    let current = item;

    if (!item.is_read) {
      await this.markOneRead(item.id);
      current = { ...item, is_read: true, read_at: new Date().toISOString() };
    }

    const userType = readNotificationUserType();
    const targetUrl = resolveNotificationNavigationUrl(current, userType);
    const currentPath = this.router.url.split('?')[0];
    const targetPath = targetUrl.split('?')[0];

    if (currentPath === targetPath) {
      this.showNotificationFeedback(current, hadDirectLink);
      return current;
    }

    const navigated = await this.router.navigateByUrl(targetUrl);
    if (!navigated) {
      this.showNotificationFeedback(current, hadDirectLink);
      return current;
    }

    if (!hadDirectLink) {
      this.toastr.info(
        current.message || '',
        this.translate.instant('notifications.center.markedRead'),
        { timeOut: 3500, progressBar: true },
      );
    }

    return current;
  }

  private showNotificationFeedback(item: UserNotificationItem, hadDirectLink: boolean): void {
    const title = hadDirectLink
      ? item.title
      : this.translate.instant('notifications.center.markedRead');
    const body = item.message || item.title;
    this.toastr.info(body, title, { timeOut: 4500, progressBar: true });
  }

  getInboxItems(): UserNotificationItem[] {
    return this.notifLog$.value.items;
  }

  setInboxUnreadCount(count: number): void {
    this.unreadCount$.next(Math.max(0, count));
  }

  markNotificationsReadByIds(ids: number[], readAt?: string): void {
    if (!ids?.length) {
      return;
    }
    const idSet = new Set(ids);
    const readTimestamp = readAt || new Date().toISOString();
    const log = this.notifLog$.value;
    const items = log.items.map((n) =>
      idSet.has(n.id) ? { ...n, is_read: true, read_at: readTimestamp } : n,
    );
    const unreadFromList = items.filter((n) => !n.is_read).length;
    this.notifLog$.next({ ...log, items });
    this.unreadCount$.next(unreadFromList);
  }

  private showBrowserBlockedToast(): void {
    this.toastr.warning(
      this.translate.instant('notifications.deviceBrowserBlocked'),
      this.translate.instant('notifications.deviceBellTitle'),
    );
  }

  private defaultStatus(): PushDeviceStatus {
    return {
      device_id: '',
      device_type: null,
      notifications_enabled: false,
      has_device_token: false,
    };
  }

  private async fetchStatus(): Promise<PushDeviceStatus | null> {
    try {
      const res = await firstValueFrom(this.http.getCurrentPushDeviceStatus());
      const httpStatus = (res as { status?: number })?.status;

      if (httpStatus === 401) {
        console.warn('[PushDevice] push-device status unauthorized — skipping');
        return null;
      }

      if (httpStatus === 200 && (res as { data?: PushDeviceStatus }).data) {
        const data = (res as { data: PushDeviceStatus }).data;
        this.status$.next(data);
        this.lastStatusAt = Date.now();
        return data;
      }
    } catch (err) {
      console.warn('[PushDevice] refreshStatus failed:', err);
    }
    return null;
  }

  private resetLocalState(): void {
    this.status$.next(null);
    this.lastStatusAt = 0;
    this.statusInFlight = null;
    this.sessionInitPromise = null;
    this.unreadCount$.next(0);
    this.notifLog$.next({
      items: [],
      total: 0,
      page: 1,
      loading: false,
      hasMore: false,
      error: null,
    });
  }

  private isUserSignedIn(): boolean {
    try {
      return !!localStorage.getItem('token');
    } catch {
      return false;
    }
  }
}
